import _ from 'lodash';
import {
  confirmLexicalStateInCTAComponent,
  confirmLexicalStateInTextComponent,
  findLayeredTextRelationByDocumentId,
} from 'modules/draftjs2lexical';
import {
  convertLexicalCTAEditorStateToHtml,
  convertLexicalTextEditorStateToHtml,
} from 'modules/Lexical/Translation';
import { call, put, select, spawn } from 'redux-saga/effects';
import { Layer, MethodName, ProcessType } from 'const';
import * as AppActions from 'containers/App/actions';
import { projectFlatFonts as projectFlatFontsSelector } from 'containers/BrandDefinition/selectors';
import { relationsOnScreens } from 'containers/Documents/selectors';
import { setActiveLayer as createSetActiveLayerAction } from 'containers/Project/actions';
import { setActiveLayer } from 'containers/Project/sagas/setActiveLayer';
import { activeLayer } from 'containers/Project/selectors';
import * as ProjectSelectors from 'containers/Project/selectors';
import { getDocumentsOnArtboards } from 'containers/Project/services/getDocumentsOnArtboards';
import { layeredRelations } from 'containers/Relations/selectors';
import { rootDocument as rootDocumentSelector } from 'containers/RootDocument/selectors';
import * as Models from 'models';
import { getTranslationPackage } from 'services/api';
import { handleSagaError } from 'services/handleError';
import logger from 'services/logger';
import { Notifications } from 'services/Notifications';
import { isCallToAction, isLayoutRendition, isTextComponent } from 'utils/entityType';
import { getTranslationPackageName } from 'utils/getPackageName';
import { getProjectPdfName } from 'utils/getProjectPdfName';
import { intlGet } from 'utils/intlGet';
import { downloadFileByUrl } from '../services/downloadFileByUrl';
import { generateProjectPdf } from '../services/generateProjectPdf';

export function* generateTranslationPackage(): Generator {
  const logId = logger.performanceStart();
  const methodName = MethodName.TRANSLATION_EXPORT;
  const previousActiveLayer = (yield select(activeLayer)) as ReturnTypeSaga<typeof activeLayer>;

  try {
    const usedRelations = (yield select(relationsOnScreens)) as ReturnTypeSaga<typeof relationsOnScreens>;
    const isLayoutRenditionOnArtboards = usedRelations.some(relation => isLayoutRendition(relation) && relation.get('documentId').some(Boolean));

    if (isLayoutRenditionOnArtboards) {
      yield spawn([logger, logger.performanceCancel], logId);

      yield call(
        [Notifications, Notifications.showNotification],
        {
          title: intlGet('Notification.Translation.ReusableLayoutPreview', 'Title'),
          message: intlGet('Notification.Translation.ReusableLayoutPreview', 'Message'),
        },
      );

      return;
    }

    yield put(AppActions.lockProjectContent(ProcessType.EXPORTING_FILES));

    const currentActiveLayer = Layer.ORIGINAL;
    // to fetch correct documents from RLs
    yield call(setActiveLayer, createSetActiveLayerAction(currentActiveLayer));

    const rootDocument = (yield select(rootDocumentSelector)) as ReturnTypeSaga<typeof rootDocumentSelector>;
    const rootDocumentName = rootDocument.get('name');
    const projectPdfName = (yield call(getProjectPdfName, rootDocumentName)) as ReturnTypeSaga<typeof getProjectPdfName>;
    const documentsList = (yield call(
      getDocumentsOnArtboards,
      ProcessType.EXPORTING_FILES,
    )) as ReturnTypeSaga<typeof getDocumentsOnArtboards>;
    const documents = documentsList.toJS() as Models.CombinedDocuments;
    const archiveName = (yield call(
      getTranslationPackageName,
      rootDocumentName,
      rootDocument.getIn(['language', '0']),
    )) as ReturnTypeSaga<typeof getTranslationPackageName>;
    const relations = (yield select(layeredRelations)) as ReturnTypeSaga<typeof layeredRelations>;
    const projectFlatFonts = (yield select(projectFlatFontsSelector)) as ReturnTypeSaga<typeof projectFlatFontsSelector>;
    const projectType = (yield select(ProjectSelectors.projectType)) as ReturnTypeSaga<typeof ProjectSelectors.projectType>;

    const textComponentHtmlById = _(documents)
      .pickBy<Models.TextComponent>(isTextComponent)
      .mapValues((textComponent) => {
        const relation = findLayeredTextRelationByDocumentId(relations, textComponent.id, currentActiveLayer);
        const styles = relation?.getIn(['styles', currentActiveLayer])?.toJS() as Models.TextRelationStyles | undefined;
        const { lexicalState } = confirmLexicalStateInTextComponent(textComponent, styles ?? null);

        return convertLexicalTextEditorStateToHtml(lexicalState ?? undefined);
      })
      .value();

    const callToActionsInnerTextById = _(documents)
      .pickBy<Models.CallToAction>(isCallToAction)
      .mapValues((ctaComponent) => {
        const relation = findLayeredTextRelationByDocumentId(relations, ctaComponent.id, currentActiveLayer);
        const { lexicalState } = confirmLexicalStateInCTAComponent(
          ctaComponent,
          relation?.getIn(['styles', currentActiveLayer])?.toJS() as Models.CallToActionStyles | undefined,
          projectType as string,
          projectFlatFonts,
        );
        const html = convertLexicalCTAEditorStateToHtml(lexicalState);

        return html ?? ctaComponent.name;
      })
      .value();

    const projectPdfUrl = (yield call(generateProjectPdf, projectPdfName)) as ReturnTypeSaga<typeof generateProjectPdf>;
    const { data: url } = (yield call(
      getTranslationPackage,
      archiveName,
      rootDocument.toJS() as Models.RootDocument,
      documents,
      textComponentHtmlById,
      callToActionsInnerTextById,
      projectPdfUrl,
    )) as ReturnTypeSaga<typeof getTranslationPackage>;

    yield spawn([logger, logger.performanceEnd], logId, { methodName });

    yield call(downloadFileByUrl, url, archiveName);
    yield call(
      [Notifications, Notifications.success],
      intlGet('Notification.Success', 'ExportTranslationPackage'),
    );
  } catch (error) {
    yield spawn([logger, logger.error], error, logId, { methodName });
    yield call(handleSagaError, error, 'Artboards.generateTranslationPackage', 'GenerateTranslationPackage');
  } finally {
    yield put(AppActions.unlockProjectContent());
    // revert user active layer
    yield call(setActiveLayer, createSetActiveLayerAction(previousActiveLayer));
  }
}
